import {
  DataStatus,
  DropdownGroup,
  DropdownItem,
  type FormControl,
  type LabelActions,
  openConfirmDialog,
  openDropdown,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {type MouseEvent} from 'react';
import {type UseFormReturn} from 'react-hook-form';

import {compose, isNil, prop, sortBy, toLower} from 'ramda';

import type {ContextTemplateResponseBody, ListContextParametersApiResponse} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  EMAIL_DIALOG_BODY_MIN_ROWS,
  EmailBodyFormField,
  isEmailHtmlEmpty,
  type useEmailBodyImageUpload,
  useTemplatesByLocale,
} from '@dms/shared';

import {type RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {FinancingEndingEditableEmailFormValues} from '../../utils/FinancingEndingEditableEmailFormSchema';
import {getFinancingEndingTemplateMessage} from '../../utils/getFinancingEndingTemplateMessage';
import {FinancingEndingSubjectField} from '../FinancingEndingSubjectField/FinancingEndingSubjectField';

export type FinancingEndingMessageSource = {
  templateId: string;
  locale: FinancingEndingEditableEmailFormValues['locale'];
};

export type FinancingEndingEditableMessageFieldsProps = RequiredTestIdProps & {
  control: FormControl<FinancingEndingEditableEmailFormValues>;
  formApi: UseFormReturn<FinancingEndingEditableEmailFormValues>;
  /** The business-case e-mail templates; the parent loads them once for the whole dialog. */
  templates: ContextTemplateResponseBody[];
  isTemplatesLoading?: boolean;
  isTemplatesError?: boolean;
  /** The placeholder catalogue; the parent loads it once for the whole dialog. */
  parameters: ListContextParametersApiResponse;
  isLoadingParameters: boolean;
  upload: Pick<
    ReturnType<typeof useEmailBodyImageUpload>,
    'handleUploadImage' | 'cancelPendingWaits'
  >;
  /** Further actions of the message label, after the template action. */
  messageLabelActions?: LabelActions;
  /** Turns the fields and their actions off, for example while the e-mail is being sent. */
  isDisabled?: boolean;
  /** Called after a template was copied into the fields, with the template it came from. */
  onTemplateApplied?: (source: FinancingEndingMessageSource) => void;
};

// Empty fields, or the unchanged text of the template they were filled from, lose nothing on replace.
const isMessageUntouched = (
  values: FinancingEndingEditableEmailFormValues,
  templates: ContextTemplateResponseBody[]
) => {
  const isEmpty = (values.subject ?? '').trim() === '' && isEmailHtmlEmpty(values.body);
  const sourceTemplate = templates.find((template) => template.id === values.templateId);
  if (isEmpty || isNil(sourceTemplate)) {
    return isEmpty;
  }
  const sourceMessage = getFinancingEndingTemplateMessage(sourceTemplate);

  return values.subject === sourceMessage.subject && values.body === sourceMessage.body;
};

/**
 * The editable Subject and Message of the financing-ending e-mail. The text is a raw source for every
 * recipient: a template only copies its subject and body here, `{{ code }}` placeholders stay as they
 * are, and nothing is rendered for one recipient. The signature is not part of the source.
 */
export function FinancingEndingEditableMessageFields(
  props: FinancingEndingEditableMessageFieldsProps
) {
  const templatesByLocale = useTemplatesByLocale(props.templates);

  // Always set with its suggestions, even before the catalogue arrives: the message editor reads its
  // token mode and its "/" menu once on mount, and the items at each keystroke.
  const templateTokens = {
    getLabel: (code: string) =>
      props.parameters.find((parameter) => parameter.code === code)?.label,
    suggestions: {
      items: sortBy(
        compose(toLower, prop('label')),
        props.parameters.map((parameter) => ({code: parameter.code, label: parameter.label}))
      ),
    },
  };

  const applyTemplate = (template: ContextTemplateResponseBody) => {
    const message = getFinancingEndingTemplateMessage(template);
    const options = {shouldDirty: true};
    props.formApi.setValue('subject', message.subject, options);
    props.formApi.setValue('body', message.body, options);
    props.formApi.setValue('templateId', message.templateId, options);
    props.formApi.setValue('locale', message.locale, options);
    props.onTemplateApplied?.({templateId: template.id, locale: message.locale});
  };

  const handleTemplatePick = (template: ContextTemplateResponseBody) => {
    if (isMessageUntouched(props.formApi.getValues(), props.templates)) {
      applyTemplate(template);
      return;
    }
    openConfirmDialog({
      text: i18n.t('general.actions.autofillTemplates.confirmDialog.message', {
        templateName: template.name,
      }),
      onConfirm: () => applyTemplate(template),
      'data-testid': suffixTestId('replaceConfirm', props),
    });
  };

  const templateAction: LabelActions[number] = {
    title: i18n.t('entity.customer.labels.chooseEmailTemplate'),
    leftIcon: 'action/autorenew',
    isDisabled: props.isDisabled,
    onClick: (event: MouseEvent) =>
      openDropdown(
        event,
        <>
          <DataStatus isLoading={props.isTemplatesLoading} isError={props.isTemplatesError} />
          {templatesByLocale.isGrouped
            ? templatesByLocale.groups.map((group) => (
                <DropdownGroup key={group.key} label={group.label}>
                  {group.templates.map((template) => (
                    <DropdownItem
                      key={template.id}
                      label={template.name}
                      onClick={() => handleTemplatePick(template)}
                    />
                  ))}
                </DropdownGroup>
              ))
            : templatesByLocale.templates.map((template) => (
                <DropdownItem
                  key={template.id}
                  label={template.name}
                  onClick={() => handleTemplatePick(template)}
                />
              ))}
        </>,
        {isHeightLimited: true}
      ),
  };

  return (
    <VStack spacing={4}>
      <FinancingEndingSubjectField
        control={props.control}
        parameters={props.parameters}
        isLoadingParameters={props.isLoadingParameters}
        isDisabled={props.isDisabled}
        data-testid={props['data-testid']}
      />
      <EmailBodyFormField
        control={props.control}
        name="body"
        labelActions={[templateAction, ...(props.messageLabelActions ?? [])]}
        upload={props.upload}
        templateTokens={templateTokens}
        minRows={EMAIL_DIALOG_BODY_MIN_ROWS}
        isDisabled={props.isDisabled}
        isRequired
        data-testid={suffixTestId('message', props)}
      />
    </VStack>
  );
}
